"use client";

import { motion } from "framer-motion";
import { useRef, useEffect } from "react";
import EditableElement from "@/components/customize/EditableElement";

export default function Journey({
  video, heading, steps = [], dateFormat = "long",
  templateMeta,
  __isMobile = false,
  __isEditable = false,
  __sectionId,
  __onFieldEdit,
  __onFieldChange,
  __onFileSelected,
  __focusFieldName,
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const playPromise = videoRef.current?.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        if (error.name !== "AbortError") {
          console.warn("Autoplay failed:", error);
        }
      });
    }
  }, [video]);

  const formatDateDisplay = (dateString, formatType) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;

    const dayNameEn = d.toLocaleDateString("en-US", { weekday: "long" });
    const monthLongEn = d.toLocaleDateString("en-US", { month: "long" });
    const monthShortEn = d.toLocaleDateString("en-US", { month: "short" });

    const dayNameId = d.toLocaleDateString("id-ID", { weekday: "long" });
    const monthLongId = d.toLocaleDateString("id-ID", { month: "long" });
    const monthShortId = d.toLocaleDateString("id-ID", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();

    const getSuffix = (n) => {
      if (n >= 11 && n <= 13) return "th";
      switch (n % 10) {
        case 1: return "st";
        case 2: return "nd";
        case 3: return "rd";
        default: return "th";
      }
    };

    switch (formatType) {
      case "short":
        return `${monthShortEn} ${day}, ${year}`;
      case "numeric":
        return `${String(day).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${year}`;
      case "full":
        return `${dayNameEn}, ${monthLongEn} ${day}${getSuffix(day)}, ${year}`;
      case "id-long":
        return `${dayNameId}, ${day} ${monthLongId} ${year}`;
      case "id-short":
        return `${day} ${monthShortId} ${year}`;
      case "long":
      default:
        return `${dayNameEn}, ${day} ${monthLongEn} ${year}`;
    }
  };
  const titleClass = __isMobile ? "text-3xl" : "text-3xl md:text-4xl";
  const stepTitleClass = __isMobile ? "text-md" : "text-md md:text-lg";
  const stepDescClass = __isMobile ? "text-xs" : "text-xs md:text-sm";
  
  const subtitle = `${templateMeta?.groomFirstName || "Groom"} & ${templateMeta?.brideFirstName || "Bride"}`;

  return (
    <div className="relative py-16 px-6 overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <EditableElement
          type="video"
          value={video}
          onChange={(val) => __onFieldChange?.(__sectionId, "video", val)}
          isEditable={__isEditable}
          fieldName="video"
          onFileSelected={(file, url) => __onFileSelected?.(`defaultSections.${__sectionId}.props.video`, file, url)}
          className="absolute inset-0 w-full h-full"
        >
          {video && (
            <video
              key={video}
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
              preload="metadata"
              aria-hidden="true"
            >
              <source src={video} type="video/mp4" />
              <track kind="captions" label="No captions available" />
            </video>
          )}
        </EditableElement>
        <div className="absolute inset-0 bg-black/40 backdrop-blur-xs" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-white">
        <div className="max-w-xl mx-auto text-center mb-10">
          {heading && (
            <EditableElement
              type="text"
              value={heading}
              onChange={(val) => __onFieldChange?.(__sectionId, "heading", val)}
              isEditable={__isEditable}
              fieldName="heading"
              onEditStart={() => __onFieldEdit?.(__sectionId, "heading")}
              isActive={__focusFieldName === "heading"}
              isMobile={__isMobile}
            >
              <h2 className={`font-bold uppercase font-cormorant tracking-wider ${titleClass}`}>
                {heading}
              </h2>
            </EditableElement>
          )}
          {subtitle && (
            <p className="text-xs font-sans tracking-widest text-white mt-2">
              {subtitle}
            </p>
          )}
        </div>

        <div className="space-y-10 max-w-xl mx-auto">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              className="relative text-center"
            >
              <EditableElement
                type="text"
                value={step.title}
                onChange={(val) => __onFieldChange?.(__sectionId, `steps[${index}].title`, val)}
                isEditable={__isEditable}
                fieldName={`steps[${index}].title`}
                onEditStart={() => __onFieldEdit?.(__sectionId, `steps[${index}].title`)}
                isActive={__focusFieldName === `steps[${index}].title`}
                isMobile={__isMobile}
              >
                <h3 className={`font-bold uppercase tracking-wider font-caudex inline-block ${stepTitleClass}`}>
                  {step.title}
                </h3>
              </EditableElement>
              <span className="block text-xs font-normal text-white/80 mt-1">
                - {formatDateDisplay(step.date, dateFormat)} -
              </span>
              <EditableElement
                type="text"
                value={step.description}
                onChange={(val) => __onFieldChange?.(__sectionId, `steps[${index}].description`, val)}
                isEditable={__isEditable}
                fieldName={`steps[${index}].description`}
                onEditStart={() => __onFieldEdit?.(__sectionId, `steps[${index}].description`)}
                isActive={__focusFieldName === `steps[${index}].description`}
                isMobile={__isMobile}
                useContentEditable={true}
                multiline={true}
              >
                <p className={`text-white mt-2 ${stepDescClass}`}>
                  {step.description}
                </p>
              </EditableElement>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
